import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, defer, switchMap, take, throwError } from 'rxjs';
import { AuthActions } from '../state/auth/auth.actions';
import { selectAccessToken } from '../state/auth/auth.selectors';
import { IS_REFRESH_TOKEN } from './auth.interceptor';
import { TokenStorageService } from './token-storage.service';

/** A request already retried after refresh. A second 401 logs out instead of refreshing again. */
export const IS_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * On 401: dispatch single-flight refresh, retry once, else force re-login.
 */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const tokens = inject(TokenStorageService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      if (req.context.get(IS_REFRESH_TOKEN) || req.url.includes('/api/v1/auth/login')) {
        return throwError(() => error);
      }
      if (req.context.get(IS_RETRIED) || !tokens.getRefreshToken()) {
        store.dispatch(AuthActions.logoutRequested({}));
        return throwError(() => error);
      }

      return defer(() => {
        store.dispatch(AuthActions.refreshRequested());
        return actions$.pipe(
          ofType(AuthActions.refreshSucceeded, AuthActions.refreshFailed),
          take(1),
          switchMap((action) => {
            if (action.type === AuthActions.refreshFailed.type) {
              return throwError(() => error);
            }
            return store.select(selectAccessToken).pipe(
              take(1),
              switchMap((token) => {
                const retry = req.clone({
                  setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
                  context: req.context.set(IS_RETRIED, true),
                });
                // `next` continues down the chain, so this interceptor does not see the retry.
                return next(retry).pipe(
                  catchError((retryError: unknown) => {
                    if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
                      store.dispatch(AuthActions.logoutRequested({}));
                    }
                    return throwError(() => retryError);
                  }),
                );
              }),
            );
          }),
        );
      });
    }),
  );
};
